import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { GitCompareArrows, CheckCircle2, AlertTriangle } from "lucide-react";
import { motion } from "framer-motion";
import { getCompanies, compareCompanies } from "../lib/api";
import { PageTitle, Panel, SectionDivider } from "../components/Panel";
import { Reveal } from "../components/Reveal";
import { DataState, EmptyState } from "../components/DataState";
import { SkeletonBlock } from "../components/LoadingSkeleton";
import { CompanySelector } from "../components/compare/CompanySelector";
import { HeadToHead } from "../components/compare/HeadToHead";
import { VerdictPanel } from "../components/compare/VerdictPanel";

const scoreColor = (v) => (v >= 80 ? "#10B981" : v >= 65 ? "#84CC16" : v >= 50 ? "#F59E0B" : v >= 35 ? "#FB923C" : "#F43F5E");

const parseNum = (s) => {
  if (typeof s !== "string") return typeof s === "number" ? s : null;
  const m = s.replace(/,/g, "").match(/-?\d+(\.\d+)?/);
  return m ? parseFloat(m[0]) : null;
};

const METRIC_ROWS = [
  { key: "market_cap", label: "Market Cap", better: "none", get: (c) => c.metrics.market_cap },
  { key: "current_price", label: "Current Price", better: "none", get: (c) => `₹${c.price?.toLocaleString("en-IN")}` },
  { key: "revenue_growth", label: "Revenue Growth", better: "high", get: (c) => c.metrics.revenue_growth },
  { key: "profit_growth", label: "Profit Growth", better: "high", get: (c) => c.metrics.profit_growth },
  { key: "operating_margin", label: "Operating Margin", better: "high", get: (c) => c.metrics.operating_margin },
  { key: "roe", label: "Return on Equity", better: "high", get: (c) => c.metrics.roe },
  { key: "roce", label: "Return on Capital", better: "high", get: (c) => c.metrics.roce },
  { key: "debt_to_equity", label: "Debt to Equity", better: "low", get: (c) => c.metrics.debt_to_equity },
  { key: "pe_ratio", label: "P/E Ratio", better: "low", get: (c) => c.metrics.pe_ratio },
  { key: "risk_level", label: "Risk Level", better: "none", get: (c) => c.scores?.risk_level },
];

export default function Compare() {
  const [params, setParams] = useSearchParams();
  const [universe, setUniverse] = useState([]);
  const [a, setA] = useState(params.get("a") || "RELIANCE");
  const [b, setB] = useState(params.get("b") || "TATAMOTORS");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { getCompanies().then((d) => setUniverse(d.companies)).catch(() => setError(true)); }, []);

  useEffect(() => {
    if (!a || !b || a === b) { setData(null); return; }
    setLoading(true); setError(false);
    const next = new URLSearchParams(); next.set("a", a); next.set("b", b); setParams(next, { replace: true });
    compareCompanies(a, b).then((d) => { setData(d); setLoading(false); }).catch(() => { setError(true); setLoading(false); });
  }, [a, b]); // eslint-disable-line

  return (
    <div className="space-y-6">
      <PageTitle
        kicker="Company Intelligence"
        title="Compare businesses. Not just stocks."
        subtitle="Put two companies side by side and understand how their businesses, performance and investment characteristics compare — LONGVIEW explains the trade-offs, it never tells you which to buy."
      />

      {/* Selection experience */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }} className="grid grid-cols-1 items-start gap-3 sm:grid-cols-[1fr_auto_1fr]">
        <CompanySelector testId="compare-select-a" label="Company A" value={a} onChange={setA} universe={universe} exclude={b} accent="amber" />
        <div className="flex items-center justify-center pt-7 sm:pt-8">
          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-[#0B0C0E] font-mono text-xs text-zinc-400">
            <GitCompareArrows className="h-4 w-4 text-amber-400" />
          </span>
        </div>
        <CompanySelector testId="compare-select-b" label="Company B" value={b} onChange={setB} universe={universe} exclude={a} accent="sky" />
      </motion.div>

      {loading && (
        <div className="space-y-6">
          <SkeletonBlock className="h-40" />
          <SkeletonBlock className="h-56" />
          <SkeletonBlock className="h-72" />
        </div>
      )}

      {error && !loading && <DataState title="Comparison temporarily unavailable" detail="LONGVIEW could not retrieve the requested company comparison. No substitute figures are being shown." onRetry={() => window.location.reload()} />}

      {!data && !loading && !error && (
        <EmptyState
          icon={GitCompareArrows}
          title="Choose two businesses to begin"
          detail="LONGVIEW will place their available intelligence — quality, growth, risk and valuation — side by side."
        />
      )}

      {data && !loading && !error && (
        <div data-testid="compare-results" className="space-y-6">
          {/* Head to head */}
          <HeadToHead a={data.a} b={data.b} dataSource={{ mode: data.is_demo ? "demo" : "production", source_label: data.data_source }} />

          {/* Business quality */}
          <Reveal>
            <Panel className="p-5 sm:p-6">
              <div className="kicker mb-4">Business Quality · LONGVIEW scores</div>
              <div className="space-y-4">
                <ScoreRow label="Business Quality" a={data.a.scores.business_quality} b={data.b.scores.business_quality} />
                <ScoreRow label="Financial Health" a={data.a.scores.financial_health} b={data.b.scores.financial_health} />
                <ScoreRow label="Growth Potential" a={data.a.scores.growth} b={data.b.scores.growth} />
                <ScoreRow label="Valuation" a={data.a.scores.valuation} b={data.b.scores.valuation} />
              </div>
              <div className="mt-4 grid grid-cols-2 gap-4 border-t border-white/[0.06] pt-4 text-center">
                <div><div className="kicker mb-1">{data.a.ticker} Risk</div><div className="font-mono text-sm font-semibold text-amber-300">{data.a.scores.risk_level}</div></div>
                <div><div className="kicker mb-1">{data.b.ticker} Risk</div><div className="font-mono text-sm font-semibold text-sky-300">{data.b.scores.risk_level}</div></div>
              </div>
            </Panel>
          </Reveal>

          {/* Key intelligence metric table */}
          <Reveal>
            <Panel data-testid="compare-metric-table" className="overflow-hidden p-0">
              <div className="border-b border-white/[0.07] px-5 py-4"><div className="kicker">Key Intelligence</div></div>
              <div className="divide-y divide-white/[0.05]">
                {METRIC_ROWS.map((row) => {
                  const av = row.get(data.a), bv = row.get(data.b);
                  const an = parseNum(av), bn = parseNum(bv);
                  let winner = null;
                  if (row.better !== "none" && an != null && bn != null && an !== bn) {
                    winner = row.better === "high" ? (an > bn ? "a" : "b") : (an < bn ? "a" : "b");
                  }
                  return (
                    <div key={row.key} className="flex flex-col gap-1.5 px-5 py-3 sm:grid sm:grid-cols-[1fr_auto_1fr] sm:items-center sm:gap-3">
                      <div className="text-center font-mono text-[10px] uppercase tracking-wider text-zinc-500 sm:hidden">{row.label}</div>
                      <div className="flex items-center justify-between sm:contents">
                        <span className={`font-mono text-sm tabular-nums sm:text-right ${winner === "a" ? "font-semibold text-emerald-400" : "text-zinc-300"}`}>{av ?? "—"}</span>
                        <div className="hidden min-w-[130px] text-center font-mono text-[10px] uppercase tracking-wider text-zinc-500 sm:block">{row.label}</div>
                        <span className={`text-right font-mono text-sm tabular-nums sm:text-left ${winner === "b" ? "font-semibold text-emerald-400" : "text-zinc-300"}`}>{bv ?? "—"}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Panel>
          </Reveal>

          {/* Strengths vs risks */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <SRColumn c={data.a} accent="amber" />
            <SRColumn c={data.b} accent="sky" />
          </div>

          <SectionDivider />

          {/* The LONGVIEW verdict */}
          <Reveal><VerdictPanel a={data.a} b={data.b} summary={data.summary} /></Reveal>

          {/* Data context */}
          <div className="rounded-md border border-white/[0.06] bg-[#0B0C0E] px-3 py-2.5">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">Status · </span>
            <span className="text-xs text-zinc-400">LONGVIEW currently uses development market data infrastructure. This comparison is for research and education — not a recommendation to buy or sell.</span>
          </div>
        </div>
      )}
    </div>
  );
}

const ScoreBarTrack = ({ value, color, align }) => (
  <div className={`flex h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800/70 ${align === "end" ? "justify-end" : ""}`}>
    <motion.div
      initial={{ width: 0 }}
      whileInView={{ width: `${value ?? 0}%` }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="h-full rounded-full"
      style={{ background: color }}
    />
  </div>
);

const ScoreRow = ({ label, a, b }) => {
  const diff = a != null && b != null ? Math.abs(a - b) : 0;
  const aLeads = a != null && b != null && a > b && diff >= 5;
  const bLeads = a != null && b != null && b > a && diff >= 5;
  return (
    <div>
      <div className="mb-1.5 flex items-center justify-between">
        <span className="flex items-center gap-1.5 font-mono text-sm font-semibold tabular-nums" style={{ color: scoreColor(a) }}>
          {a ?? "—"}
          {aLeads && <span className="rounded border border-amber-500/25 bg-amber-500/10 px-1 py-0.5 font-mono text-[8px] uppercase tracking-wider text-amber-300">Leads</span>}
        </span>
        <span className="font-mono text-[11px] uppercase tracking-wider text-zinc-400">{label}</span>
        <span className="flex items-center gap-1.5 font-mono text-sm font-semibold tabular-nums" style={{ color: scoreColor(b) }}>
          {bLeads && <span className="rounded border border-sky-500/25 bg-sky-500/10 px-1 py-0.5 font-mono text-[8px] uppercase tracking-wider text-sky-300">Leads</span>}
          {b ?? "—"}
        </span>
      </div>
      <div className="flex items-center gap-1">
        <ScoreBarTrack value={a} color={scoreColor(a)} align="end" />
        <ScoreBarTrack value={b} color={scoreColor(b)} />
      </div>
    </div>
  );
};

const SRColumn = ({ c, accent }) => (
  <Reveal>
    <Panel className="p-5">
      <div className="mb-3 flex items-center gap-2">
        <span className={`rounded border px-2 py-0.5 font-mono text-xs font-bold ${accent === "amber" ? "border-amber-500/30 text-amber-300" : "border-sky-500/30 text-sky-300"}`}>{c.ticker}</span>
      </div>
      <div className="kicker mb-2 flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" /> Strengths</div>
      <ul className="mb-4 space-y-2">
        {c.strengths.map((s, i) => <li key={i} className="flex gap-2 text-xs leading-relaxed text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />{s}</li>)}
      </ul>
      <div className="kicker mb-2 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5 text-rose-400" /> Risks</div>
      <ul className="space-y-2">
        {c.risks.map((s, i) => <li key={i} className="flex gap-2 text-xs leading-relaxed text-zinc-300"><span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-rose-500" />{s}</li>)}
      </ul>
    </Panel>
  </Reveal>
);
